import React, { useState, useEffect, useRef } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { toast } from 'react-toastify';
import { analyzeGameWithStockfish, MoveAnalysis, AnalysisResult } from '../../../../utils/stockfishAnalysis';
import { trackChequeredGameAnalysisViewed, trackChequeredGameAnalysisStarted, trackChequeredGameAnalysisCompleted } from '../../../../utils/analytics';

interface AnalyzeGameViewProps {
    serviceProviderId: string;
    loggedInUserId: string;
}

const AnalyzeGameView: React.FC<AnalyzeGameViewProps> = ({ serviceProviderId, loggedInUserId }) => {
    const [pgn, setPgn] = useState('');
    const [moves, setMoves] = useState<string[]>([]);
    const [game, setGame] = useState<Chess | null>(null);
    const [currentMoveIndex, setCurrentMoveIndex] = useState(-1);
    const [analysis, setAnalysis] = useState<MoveAnalysis[]>([]);
    const [analysisSummary, setAnalysisSummary] = useState<AnalysisResult['summary'] | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [startingFen, setStartingFen] = useState<string>('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
    const [autoAnalyze, setAutoAnalyze] = useState(true);
    const [analysisProgress, setAnalysisProgress] = useState<string>('');
    const isAnalyzingRef = useRef(false); // Ref to track if analysis is in progress
    const analysisTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref to track timeout

    // Add CSS animation for spinner
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (analysisTimeoutRef.current) {
                clearTimeout(analysisTimeoutRef.current);
            }
        };
    }, []);

    // Analytics: game analysis page viewed (once on mount)
    useEffect(() => {
        if (!serviceProviderId || !loggedInUserId) return;
        trackChequeredGameAnalysisViewed({
            service_provider_id: serviceProviderId,
            user_id: loggedInUserId
        });
    }, [serviceProviderId, loggedInUserId]);

    /**
     * Clean PGN by removing comments and annotations
     * Removes: {comments}, [annotations], ; comments, and variations (moves in parentheses)
     * Preserves: FEN tags and standard PGN tags
     */
    const cleanPGN = (pgnText: string): string => {
        let cleaned = pgnText;
        
        // Remove comments in curly braces {comment}
        cleaned = cleaned.replace(/\{[^}]*\}/g, '');
        
        // Remove annotations in square brackets [%eval 0.5] or [%clk 0:00:01]
        // But preserve FEN tags [FEN "..."]
        cleaned = cleaned.replace(/\[%[^\]]*\]/g, '');
        
        // Remove comments starting with semicolon (line comments)
        cleaned = cleaned.split('\n')
            .map(line => line.split(';')[0]) // Take everything before semicolon
            .join('\n');
        
        // Remove variations (moves in parentheses)
        cleaned = cleaned.replace(/\([^)]*\)/g, '');
        
        // Clean up multiple spaces but preserve line breaks for tags
        cleaned = cleaned.replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space
        cleaned = cleaned.replace(/\n\s*\n/g, '\n'); // Remove empty lines
        
        return cleaned.trim();
    };

    /**
     * Extract FEN from PGN tags if present
     */
    const extractFENFromPGN = (pgnText: string): string | null => {
        const fenMatch = pgnText.match(/\[FEN\s+"([^"]+)"\]/i);
        if (fenMatch && fenMatch[1]) {
            return fenMatch[1];
        }
        return null;
    };

    const loadGameFromPGN = (pgnText: string) => {
        try {
            // Extract starting FEN if present (before cleaning)
            const extractedFen = extractFENFromPGN(pgnText);
            const initialFen = extractedFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
            
            // Clean PGN to remove comments (preserves FEN tags)
            const cleanedPGN = cleanPGN(pgnText);
            
            // Create game with starting position
            const newGame = new Chess(initialFen);
            
            // Load the cleaned PGN (chess.js will handle FEN tag if present, but we've already set it)
            // If PGN has moves, load them; if it's just a position, the game is already set
            if (cleanedPGN.trim() && !cleanedPGN.match(/^\[.*\]\s*$/)) {
                // Only load PGN if it contains moves (not just tags)
                newGame.loadPgn(cleanedPGN);
            }
            
            const gameMoves = newGame.history();
            setGame(newGame);
            setMoves(gameMoves);
            setCurrentMoveIndex(gameMoves.length - 1);
            setStartingFen(initialFen);
            setError(null);
            return true;
        } catch (err) {
            setError('Invalid PGN format. Please check your game notation.');
            console.error('PGN loading error:', err);
            return false;
        }
    };

    const handlePGNSubmit = async () => {
        if (!pgn.trim()) {
            setError('Please enter a PGN');
            return;
        }
        
        // Clear any pending analysis timeout
        if (analysisTimeoutRef.current) {
            clearTimeout(analysisTimeoutRef.current);
            analysisTimeoutRef.current = null;
        }
        
        if (loadGameFromPGN(pgn)) {
            setAnalysis([]);
            setAnalysisSummary(null);
            
            // Auto-analyze if enabled
            if (autoAnalyze) {
                // Small delay to let the UI update, but only if not already analyzing
                analysisTimeoutRef.current = setTimeout(() => {
                    if (!isAnalyzingRef.current) {
                        handleAnalyze();
                    }
                    analysisTimeoutRef.current = null;
                }, 100);
            }
        }
    };

    // Analyze game using Stockfish API
    const analyzeGame = async (gameMoves: string[]): Promise<AnalysisResult> => {
        if (!game) {
            throw new Error('Game not loaded');
        }
        
        // Use the starting FEN (extracted from PGN or default)
        // Use Stockfish analysis
        return await analyzeGameWithStockfish(gameMoves, startingFen, 15);
    };

    const handleAnalyze = async () => {
        if (moves.length === 0) {
            setError('No moves to analyze. Please load a game first.');
            return;
        }

        // Prevent multiple simultaneous analysis calls
        if (isAnalyzingRef.current) {
            console.log('[Analysis] Already analyzing, skipping duplicate call');
            return;
        }

        isAnalyzingRef.current = true;
        setIsAnalyzing(true);
        setError(null);
        setAnalysisProgress(`Analyzing ${moves.length} moves with Stockfish batch API...`);

        trackChequeredGameAnalysisStarted({
            service_provider_id: serviceProviderId,
            user_id: loggedInUserId,
            move_count: moves.length,
            has_fen: startingFen !== 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
        });

        try {
            console.log('[Analysis] Starting analysis for', moves.length, 'moves');
            const result = await analyzeGame(moves);
            console.log('[Analysis] Analysis complete:', result.analyses.length, 'moves analyzed');
            setAnalysis(result.analyses);
            setAnalysisSummary(result.summary || null);

            const summary = result.summary;
            const accuracyNum = summary?.accuracy ? parseFloat(String(summary.accuracy).replace('%', '')) : undefined;
            trackChequeredGameAnalysisCompleted({
                service_provider_id: serviceProviderId,
                user_id: loggedInUserId,
                move_count: result.analyses.length,
                accuracy: accuracyNum,
                blunders: summary?.blunders,
                mistakes: summary?.mistakes
            });

            if (summary) {
                toast.success(summary.summaryText || 'Analysis complete!');
            } else {
                // Fallback: count mistakes for success message
                const blunders = result.analyses.filter(a => a.isBlunder).length;
                const mistakes = result.analyses.filter(a => a.isMistake).length;
                const inaccuracies = result.analyses.filter(a => a.isInaccuracy).length;
                
                let message = 'Analysis complete! ';
                if (blunders > 0) message += `${blunders} blunder${blunders > 1 ? 's' : ''}, `;
                if (mistakes > 0) message += `${mistakes} mistake${mistakes > 1 ? 's' : ''}, `;
                if (inaccuracies > 0) message += `${inaccuracies} inaccuracy${inaccuracies > 1 ? 'ies' : ''}`;
                
                toast.success(message);
            }
            setAnalysisProgress('');
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to analyze game';
            console.error('[Analysis] Error:', err);
            setError(errorMsg);
            toast.error(errorMsg);
            setAnalysisProgress('');
        } finally {
            setIsAnalyzing(false);
            isAnalyzingRef.current = false;
        }
    };

    const goToMove = (index: number) => {
        if (!game) return;
        const tempGame = new Chess();
        for (let i = 0; i <= index && i < moves.length; i++) {
            tempGame.move(moves[i]);
        }
        setCurrentMoveIndex(index);
        setGame(new Chess(tempGame.fen()));
    };

    const getMoveEvaluation = (index: number): MoveAnalysis | null => {
        return analysis[index] || null;
    };

    const currentFen = game?.fen() || startingFen;

    return (
        <div className="analyze-container" style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
            <div className="analyze-header" style={{ 
                padding: '1.5rem 2rem', 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                flexShrink: 0,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.4)'
                    }}>
                        🎯
                    </div>
                    <div>
                        <h1 style={{ 
                            fontSize: '1.75rem', 
                            fontWeight: 700, 
                            color: '#111827', 
                            margin: 0, 
                            marginBottom: '0.25rem',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Game Analysis
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                            AI-powered move analysis powered by Stockfish Engine
                        </p>
                    </div>
                </div>
            </div>

            <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                gap: '1.5rem',
                padding: '1.5rem',
                overflow: 'hidden'
            }}>
                {/* Top Section - Overall Analysis Summary (Always Visible) */}
                {analysis.length > 0 && (
                    <section className="analysis-summary-top" style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '16px',
                        padding: '2rem',
                        border: 'none',
                        color: 'white',
                        boxShadow: '0 10px 25px -5px rgba(102, 126, 234, 0.4), 0 10px 10px -5px rgba(102, 126, 234, 0.2)',
                        flexShrink: 0
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                            <h2 style={{ 
                                fontSize: '1.5rem', 
                                fontWeight: 700, 
                                color: 'white', 
                                margin: 0,
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.75rem' 
                            }}>
                                <span style={{ fontSize: '2rem' }}>📊</span>
                                Game Analysis Summary
                            </h2>
                            {analysisSummary?.summaryText && (
                                <div style={{ 
                                    padding: '0.75rem 1.5rem',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '12px',
                                    fontSize: '0.875rem',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    fontWeight: 600,
                                    maxWidth: '50%',
                                    textAlign: 'center'
                                }}>
                                    {analysisSummary.summaryText}
                                </div>
                            )}
                        </div>
                        <div className="analysis-stats" style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: '1rem'
                        }}>
                            <div style={{ 
                                padding: '1.5rem',
                                background: 'rgba(255, 255, 255, 0.25)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                transition: 'transform 0.2s',
                                textAlign: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.5rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Accuracy</div>
                                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'white' }}>
                                    {analysisSummary?.accuracy 
                                        ? `${parseFloat(analysisSummary.accuracy.replace('%', '')).toFixed(0)}%`
                                        : `${Math.round((analysis.filter((a) => a.isBestMove).length / moves.length) * 100)}%`}
                                </div>
                            </div>
                            <div style={{ 
                                padding: '1.5rem',
                                background: 'rgba(16, 185, 129, 0.3)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                transition: 'transform 0.2s',
                                textAlign: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.5rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>✅ Best Moves</div>
                                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'white' }}>
                                    {analysisSummary?.bestMoves ?? analysis.filter((a) => a.isBestMove).length}
                                </div>
                            </div>
                            <div style={{ 
                                padding: '1.5rem',
                                background: 'rgba(234, 88, 12, 0.3)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                transition: 'transform 0.2s',
                                textAlign: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.5rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>⚠️ Mistakes</div>
                                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'white' }}>
                                    {analysisSummary?.mistakes ?? analysis.filter((a) => a.isMistake).length}
                                </div>
                            </div>
                            <div style={{ 
                                padding: '1.5rem',
                                background: 'rgba(220, 38, 38, 0.3)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                transition: 'transform 0.2s',
                                textAlign: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.5rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>❌ Blunders</div>
                                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'white' }}>
                                    {analysisSummary?.blunders ?? analysis.filter((a) => a.isBlunder).length}
                                </div>
                            </div>
                            <div style={{ 
                                padding: '1.5rem',
                                background: 'rgba(245, 158, 11, 0.3)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                transition: 'transform 0.2s',
                                textAlign: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.5rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>⚡ Inaccuracies</div>
                                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'white' }}>
                                    {analysisSummary?.inaccuracies ?? analysis.filter((a) => a.isInaccuracy).length}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Main Content Area - Split Layout */}
                <div style={{ 
                    flex: 1,
                    display: 'flex',
                    gap: '1.5rem',
                    overflow: 'hidden',
                    minHeight: 0
                }}>
                    {/* Left Panel - Mistakes Section (Most Important) */}
                    <div className="analyze-left" style={{ 
                        flex: '1 1 50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        overflowY: 'auto',
                        paddingRight: '0.5rem',
                        minWidth: 0
                    }}>
                        {/* PGN Input Section - Compact */}
                        {(!analysis.length || moves.length === 0) && (
                            <section className="analyze-section" style={{ 
                                background: 'rgba(255, 255, 255, 0.98)',
                                borderRadius: '16px',
                                padding: '1.5rem',
                                border: 'none',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                backdropFilter: 'blur(10px)',
                                flexShrink: 0
                            }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.3)'
                            }}>
                                📝
                            </div>
                            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                Upload Game (PGN)
                            </h2>
                        </div>
                        <textarea
                            className="pgn-input"
                            placeholder={`Paste your game in PGN format here...

Example PGN:
[Event "Example Game"]
[Site "Chess.com"]
[Date "2024.01.01"]
[White "Player 1"]
[Black "Player 2"]
[Result "1-0"]

1. e4 e5 2. Nf3 Nc6 3. Bb5 a6 4. Ba4 Nf6 5. O-O Be7 6. Re1 b5 7. Bb3 d6 8. c3 O-O 9. h3 Nb8 10. d4 Nbd7 11. c4 c6 12. cxb5 axb5 13. Nc3 b4 14. Nb1 Bb7 15. b3 c5 16. d5 c4 17. Nbd2 Nc5 18. a4 cxb3 19. Nxb3 Nxb3 20. Qxb3 bxa4 21. Qxa4 Bd7 22. Ra3 Rfc8 23. Qxa7 Rxa7 24. Rxa7 Bc6 25. e5 dxe5 26. Bxe5 Bxd5 27. Nxd5 Nxd5 28. Bxh7+ Kxh7 29. Rxd5 Kg6 30. Rd6+ f6 31. Bxf6 gxf6 32. Rxf6+ Kg7 33. Rg6+ Kh7 34. Rg7# 1-0`}
                            value={pgn}
                            onChange={(e) => setPgn(e.target.value)}
                            rows={10}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '12px',
                                fontSize: '0.875rem',
                                fontFamily: 'monospace',
                                resize: 'vertical',
                                transition: 'all 0.2s',
                                background: '#fafafa'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#667eea';
                                e.target.style.background = '#ffffff';
                                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.background = '#fafafa';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        <div style={{ marginTop: '1rem' }}>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                fontSize: '0.875rem',
                                color: '#374151',
                                marginBottom: '1rem',
                                cursor: 'pointer',
                                padding: '0.75rem',
                                background: '#f9fafb',
                                borderRadius: '8px',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#f9fafb'}
                            >
                                <input
                                    type="checkbox"
                                    checked={autoAnalyze}
                                    onChange={(e) => setAutoAnalyze(e.target.checked)}
                                    style={{ 
                                        cursor: 'pointer',
                                        width: '18px',
                                        height: '18px',
                                        accentColor: '#667eea'
                                    }}
                                />
                                <span style={{ fontWeight: 500 }}>Auto-analyze after loading game</span>
                            </label>
                            <div className="analyze-actions" style={{ 
                                display: 'flex', 
                                gap: '0.75rem'
                            }}>
                                <button 
                                    type="button" 
                                    onClick={handlePGNSubmit} 
                                    disabled={isAnalyzing}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem 1.25rem',
                                        background: isAnalyzing ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                                        boxShadow: isAnalyzing ? 'none' : '0 4px 6px -1px rgba(102, 126, 234, 0.4)',
                                        transition: 'all 0.2s',
                                        transform: isAnalyzing ? 'none' : 'translateY(0)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isAnalyzing) {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 12px -1px rgba(102, 126, 234, 0.5)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isAnalyzing) {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(102, 126, 234, 0.4)';
                                        }
                                    }}
                                >
                                    {autoAnalyze && isAnalyzing ? '⏳ Loading...' : '📥 Load Game'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAnalyze}
                                    disabled={isAnalyzing || moves.length === 0}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem 1.25rem',
                                        background: isAnalyzing || moves.length === 0 ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        cursor: isAnalyzing || moves.length === 0 ? 'not-allowed' : 'pointer',
                                        boxShadow: isAnalyzing || moves.length === 0 ? 'none' : '0 4px 6px -1px rgba(16, 185, 129, 0.4)',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isAnalyzing && moves.length > 0) {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 12px -1px rgba(16, 185, 129, 0.5)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isAnalyzing && moves.length > 0) {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(16, 185, 129, 0.4)';
                                        }
                                    }}
                                >
                                    {isAnalyzing ? '⏳ Analyzing...' : '🔍 Analyze'}
                                </button>
                            </div>
                        </div>
                        {analysisProgress && (
                            <div style={{ 
                                marginTop: '1rem',
                                padding: '1rem',
                                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                color: '#1e40af',
                                borderRadius: '12px',
                                fontSize: '0.875rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                border: '1px solid #93c5fd',
                                boxShadow: '0 2px 4px rgba(30, 64, 175, 0.1)'
                            }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    border: '3px solid #3b82f6',
                                    borderTopColor: 'transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }} />
                                <span style={{ fontWeight: 500 }}>{analysisProgress}</span>
                            </div>
                        )}
                        {error && (
                            <div style={{ 
                                marginTop: '1rem',
                                padding: '1rem',
                                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                color: '#991b1b',
                                borderRadius: '12px',
                                fontSize: '0.875rem',
                                border: '1px solid #fca5a5',
                                boxShadow: '0 2px 4px rgba(153, 27, 27, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                                <span style={{ fontWeight: 500 }}>{error}</span>
                            </div>
                        )}
                            </section>
                        )}

                        {/* All Mistakes Section - Most Important (Show after analysis) */}
                        {analysis.length > 0 && (analysis.filter(a => a.isBlunder || a.isMistake || a.isInaccuracy).length > 0) ? (
                            <section className="analyze-section" style={{ 
                                background: 'rgba(255, 255, 255, 0.98)',
                                borderRadius: '16px',
                                padding: '2rem',
                                border: 'none',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                backdropFilter: 'blur(10px)',
                                flex: '1 1 auto',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', flexShrink: 0 }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.3)'
                                    }}>
                                        ⚠️
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0, marginBottom: '0.25rem' }}>
                                            Mistakes & Errors
                                        </h2>
                                        <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                                            {analysis.filter(a => a.isBlunder || a.isMistake || a.isInaccuracy).length} errors found - Click any to jump to that move
                                        </p>
                                    </div>
                                </div>

                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '1rem',
                                    overflowY: 'auto',
                                    flex: '1 1 auto',
                                    minHeight: 0,
                                    paddingRight: '0.5rem'
                                }}>
                                    {/* Blunders First */}
                                    {analysis
                                        .map((a, idx) => ({ ...a, originalIndex: a.moveIndex !== undefined ? a.moveIndex : idx }))
                                        .filter(a => a.isBlunder)
                                        .map((moveAnalysis) => {
                                            const moveIdx = moveAnalysis.originalIndex!;
                                            const moveNumber = Math.floor(moveIdx / 2) + 1;
                                            const isWhite = moveIdx % 2 === 0;
                                            const moveNotation = isWhite ? `${moveNumber}. ${moveAnalysis.move}` : `${moveNumber}... ${moveAnalysis.move}`;
                                            
                                            return (
                                                <div
                                                    key={`blunder-${moveIdx}`}
                                                    onClick={() => goToMove(moveIdx)}
                                                    style={{
                                                        padding: '1.25rem',
                                                        background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                                        border: '2px solid #fca5a5',
                                                        borderRadius: '12px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.1)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.2)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.1)';
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                        <span style={{
                                                            padding: '0.25rem 0.75rem',
                                                            background: '#dc2626',
                                                            color: 'white',
                                                            borderRadius: '6px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            ❌ Blunder
                                                        </span>
                                                        <span style={{
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            color: '#991b1b',
                                                            fontFamily: 'monospace'
                                                        }}>
                                                            Move {moveIdx + 1}: {moveNotation}
                                                        </span>
                                                        {moveAnalysis.bestMove && (
                                                            <span style={{
                                                                fontSize: '0.875rem',
                                                                color: '#7f1d1d',
                                                                fontWeight: 500
                                                            }}>
                                                                Best: {moveAnalysis.bestMove}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {moveAnalysis.analysis && (
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            color: '#991b1b',
                                                            lineHeight: '1.6',
                                                            marginBottom: '0.5rem',
                                                            fontWeight: 500
                                                        }}>
                                                            {moveAnalysis.analysis}
                                                        </div>
                                                    )}
                                                    {moveAnalysis.evaluationDescription && (
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            color: '#7f1d1d',
                                                            fontStyle: 'italic',
                                                            marginTop: '0.5rem',
                                                            padding: '0.5rem',
                                                            background: 'rgba(255, 255, 255, 0.5)',
                                                            borderRadius: '6px'
                                                        }}>
                                                            {moveAnalysis.evaluationDescription}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                    {/* Mistakes */}
                                    {analysis
                                        .map((a, idx) => ({ ...a, originalIndex: a.moveIndex !== undefined ? a.moveIndex : idx }))
                                        .filter(a => a.isMistake && !a.isBlunder)
                                        .map((moveAnalysis) => {
                                            const moveIdx = moveAnalysis.originalIndex!;
                                            const moveNumber = Math.floor(moveIdx / 2) + 1;
                                            const isWhite = moveIdx % 2 === 0;
                                            const moveNotation = isWhite ? `${moveNumber}. ${moveAnalysis.move}` : `${moveNumber}... ${moveAnalysis.move}`;
                                            
                                            return (
                                                <div
                                                    key={`mistake-${moveIdx}`}
                                                    onClick={() => goToMove(moveIdx)}
                                                    style={{
                                                        padding: '1.25rem',
                                                        background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
                                                        border: '2px solid #fb923c',
                                                        borderRadius: '12px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        boxShadow: '0 2px 4px rgba(234, 88, 12, 0.1)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(234, 88, 12, 0.2)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(234, 88, 12, 0.1)';
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                        <span style={{
                                                            padding: '0.25rem 0.75rem',
                                                            background: '#ea580c',
                                                            color: 'white',
                                                            borderRadius: '6px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            ⚠️ Mistake
                                                        </span>
                                                        <span style={{
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            color: '#ea580c',
                                                            fontFamily: 'monospace'
                                                        }}>
                                                            Move {moveIdx + 1}: {moveNotation}
                                                        </span>
                                                        {moveAnalysis.bestMove && (
                                                            <span style={{
                                                                fontSize: '0.875rem',
                                                                color: '#9a3412',
                                                                fontWeight: 500
                                                            }}>
                                                                Best: {moveAnalysis.bestMove}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {moveAnalysis.analysis && (
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            color: '#ea580c',
                                                            lineHeight: '1.6',
                                                            marginBottom: '0.5rem',
                                                            fontWeight: 500
                                                        }}>
                                                            {moveAnalysis.analysis}
                                                        </div>
                                                    )}
                                                    {moveAnalysis.evaluationDescription && (
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            color: '#9a3412',
                                                            fontStyle: 'italic',
                                                            marginTop: '0.5rem',
                                                            padding: '0.5rem',
                                                            background: 'rgba(255, 255, 255, 0.5)',
                                                            borderRadius: '6px'
                                                        }}>
                                                            {moveAnalysis.evaluationDescription}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                    {/* Inaccuracies */}
                                    {analysis
                                        .map((a, idx) => ({ ...a, originalIndex: a.moveIndex !== undefined ? a.moveIndex : idx }))
                                        .filter(a => a.isInaccuracy && !a.isMistake && !a.isBlunder)
                                        .map((moveAnalysis) => {
                                            const moveIdx = moveAnalysis.originalIndex!;
                                            const moveNumber = Math.floor(moveIdx / 2) + 1;
                                            const isWhite = moveIdx % 2 === 0;
                                            const moveNotation = isWhite ? `${moveNumber}. ${moveAnalysis.move}` : `${moveNumber}... ${moveAnalysis.move}`;
                                            
                                            return (
                                                <div
                                                    key={`inaccuracy-${moveIdx}`}
                                                    onClick={() => goToMove(moveIdx)}
                                                    style={{
                                                        padding: '1.25rem',
                                                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                                        border: '2px solid #fcd34d',
                                                        borderRadius: '12px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        boxShadow: '0 2px 4px rgba(245, 158, 11, 0.1)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(245, 158, 11, 0.2)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(245, 158, 11, 0.1)';
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                        <span style={{
                                                            padding: '0.25rem 0.75rem',
                                                            background: '#f59e0b',
                                                            color: 'white',
                                                            borderRadius: '6px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            ⚡ Inaccuracy
                                                        </span>
                                                        <span style={{
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            color: '#f59e0b',
                                                            fontFamily: 'monospace'
                                                        }}>
                                                            Move {moveIdx + 1}: {moveNotation}
                                                        </span>
                                                        {moveAnalysis.bestMove && (
                                                            <span style={{
                                                                fontSize: '0.875rem',
                                                                color: '#92400e',
                                                                fontWeight: 500
                                                            }}>
                                                                Best: {moveAnalysis.bestMove}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {moveAnalysis.analysis && (
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            color: '#f59e0b',
                                                            lineHeight: '1.6',
                                                            marginBottom: '0.5rem',
                                                            fontWeight: 500
                                                        }}>
                                                            {moveAnalysis.analysis}
                                                        </div>
                                                    )}
                                                    {moveAnalysis.evaluationDescription && (
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            color: '#92400e',
                                                            fontStyle: 'italic',
                                                            marginTop: '0.5rem',
                                                            padding: '0.5rem',
                                                            background: 'rgba(255, 255, 255, 0.5)',
                                                            borderRadius: '6px'
                                                        }}>
                                                            {moveAnalysis.evaluationDescription}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </section>
                        ) : (
                            <section className="analyze-section" style={{ 
                                background: 'rgba(255, 255, 255, 0.98)',
                                borderRadius: '16px',
                                padding: '2rem',
                                border: 'none',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                backdropFilter: 'blur(10px)',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>
                                    Excellent Game!
                                </h2>
                                <p style={{ fontSize: '1rem', color: '#6b7280' }}>
                                    No mistakes, blunders, or inaccuracies detected. Great play!
                                </p>
                            </section>
                        )}

                        {/* Moves List Section - Collapsible */}
                        {moves.length > 0 && (
                            <section className="analyze-section" style={{ 
                                background: 'rgba(255, 255, 255, 0.98)',
                                borderRadius: '16px',
                                padding: '1.5rem',
                                border: 'none',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                backdropFilter: 'blur(10px)',
                                flexShrink: 0
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)'
                                    }}>
                                        📋
                                    </div>
                                    <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                        Game Moves ({moves.length})
                                    </h2>
                                </div>
                                <div className="moves-list" style={{ 
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: '0.5rem',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    paddingRight: '0.5rem'
                                }}>
                                    {moves.map((move, index) => {
                                        const moveAnalysis = getMoveEvaluation(index);
                                        const moveNumber = Math.floor(index / 2) + 1;
                                        const isWhite = index % 2 === 0;
                                        const moveNotation = isWhite ? `${moveNumber}. ${move}` : move;
                                        
                                        const getMoveStyle = () => {
                                            const baseStyle: React.CSSProperties = {
                                                padding: '0.625rem',
                                                textAlign: 'left',
                                                border: '2px solid',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                fontSize: '0.8125rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                fontWeight: 500,
                                                transition: 'all 0.2s',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            };
                                            
                                            if (currentMoveIndex === index) {
                                                baseStyle.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                                                baseStyle.color = 'white';
                                                baseStyle.borderColor = '#667eea';
                                                baseStyle.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                                                baseStyle.transform = 'scale(1.02)';
                                            } else if (moveAnalysis?.isBlunder) {
                                                baseStyle.background = '#fee2e2';
                                                baseStyle.color = '#991b1b';
                                                baseStyle.borderColor = '#fca5a5';
                                            } else if (moveAnalysis?.isMistake) {
                                                baseStyle.background = '#fed7aa';
                                                baseStyle.color = '#ea580c';
                                                baseStyle.borderColor = '#fdba74';
                                            } else if (moveAnalysis?.isInaccuracy) {
                                                baseStyle.background = '#fef3c7';
                                                baseStyle.color = '#f59e0b';
                                                baseStyle.borderColor = '#fde68a';
                                            } else if (moveAnalysis?.isBestMove) {
                                                baseStyle.background = '#dcfce7';
                                                baseStyle.color = '#166534';
                                                baseStyle.borderColor = '#bbf7d0';
                                            } else {
                                                baseStyle.background = '#f9fafb';
                                                baseStyle.color = '#111827';
                                                baseStyle.borderColor = '#e5e7eb';
                                            }
                                            
                                            return baseStyle;
                                        };

                                        return (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => goToMove(index)}
                                                style={getMoveStyle()}
                                                onMouseEnter={(e) => {
                                                    if (currentMoveIndex !== index) {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (currentMoveIndex !== index) {
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }
                                                }}
                                            >
                                                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{moveNotation}</span>
                                                {moveAnalysis && (
                                                    <span style={{ 
                                                        marginLeft: '0.5rem',
                                                        fontSize: '0.875rem',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}>
                                                        {moveAnalysis.isBlunder && '❌'}
                                                        {moveAnalysis.isMistake && !moveAnalysis.isBlunder && '⚠️'}
                                                        {moveAnalysis.isInaccuracy && !moveAnalysis.isMistake && !moveAnalysis.isBlunder && '⚡'}
                                                        {moveAnalysis.isBestMove && !moveAnalysis.isInaccuracy && !moveAnalysis.isMistake && !moveAnalysis.isBlunder && '✅'}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Right Panel - Board and Move Analysis */}
                    <div className="analyze-right" style={{ 
                        flex: '1 1 50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        overflowY: 'auto',
                        paddingRight: '0.5rem',
                        minWidth: 0
                    }}>
                        <section className="board-section" style={{ 
                            background: 'rgba(255, 255, 255, 0.98)',
                            borderRadius: '16px',
                            padding: '1.5rem',
                            border: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: '1 1 auto',
                            minHeight: 0,
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            backdropFilter: 'blur(10px)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexShrink: 0 }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '20px',
                                    boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)'
                                }}>
                                    ♟️
                                </div>
                                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                    Current Position
                                </h2>
                            </div>
                            <div className="board-container" style={{ 
                                width: '100%',
                                flex: '1 1 auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 0,
                                padding: '1rem',
                                overflow: 'visible'
                            }}>
                                <Chessboard 
                                    position={currentFen} 
                                    boardWidth={Math.min(650, Math.min(window.innerWidth * 0.5, window.innerHeight * 0.65))} 
                                />
                            </div>
                            {currentMoveIndex >= 0 && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '0.75rem 1.25rem',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    borderRadius: '10px',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    flexShrink: 0
                                }}>
                                    Move {currentMoveIndex + 1} of {moves.length}
                                </div>
                            )}
                        </section>

                        {/* Move Analysis Panel */}
                        {currentMoveIndex >= 0 && analysis[currentMoveIndex] && (
                            <div className="analysis-panel" style={{ 
                                flex: '1 1 auto',
                                display: 'flex',
                                flexDirection: 'column',
                                background: 'rgba(255, 255, 255, 0.98)',
                                borderRadius: '16px',
                                border: 'none',
                                padding: '1.5rem',
                                overflowY: 'auto',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                backdropFilter: 'blur(10px)',
                                minHeight: 0
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        boxShadow: '0 4px 6px -1px rgba(236, 72, 153, 0.3)'
                                    }}>
                                        🔍
                                    </div>
                                    <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                        Move Analysis
                                    </h2>
                                </div>
                                
                                <div className="move-info" style={{ marginBottom: '1.5rem' }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.75rem',
                                        marginBottom: '1rem',
                                        padding: '1rem',
                                        background: '#f9fafb',
                                        borderRadius: '12px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        <span style={{ 
                                            fontSize: '0.875rem', 
                                            color: '#6b7280',
                                            fontWeight: 500
                                        }}>
                                            Move {currentMoveIndex + 1}:
                                        </span>
                                        <span style={{ 
                                            fontSize: '1.25rem', 
                                            fontWeight: 700,
                                            color: '#111827',
                                            fontFamily: 'monospace',
                                            padding: '0.5rem 1rem',
                                            background: 'white',
                                            borderRadius: '8px',
                                            border: '2px solid #e5e7eb'
                                        }}>
                                            {moves[currentMoveIndex]}
                                        </span>
                                        {analysis[currentMoveIndex].isBlunder && (
                                            <span style={{ 
                                                padding: '0.5rem 1rem',
                                                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                                color: '#991b1b',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                                            }}>
                                                ❌ BLUNDER
                                            </span>
                                        )}
                                        {analysis[currentMoveIndex].isMistake && !analysis[currentMoveIndex].isBlunder && (
                                            <span style={{ 
                                                padding: '0.5rem 1rem',
                                                background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
                                                color: '#ea580c',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                boxShadow: '0 2px 4px rgba(234, 88, 12, 0.2)'
                                            }}>
                                                ⚠️ MISTAKE
                                            </span>
                                        )}
                                        {analysis[currentMoveIndex].isInaccuracy && (
                                            <span style={{ 
                                                padding: '0.5rem 1rem',
                                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                                color: '#f59e0b',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
                                            }}>
                                                ⚡ INACCURACY
                                            </span>
                                        )}
                                        {analysis[currentMoveIndex].isBestMove && (
                                            <span style={{ 
                                                padding: '0.5rem 1rem',
                                                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                                color: '#16a34a',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)'
                                            }}>
                                                ✅ BEST MOVE
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {analysis[currentMoveIndex].bestMove && analysis[currentMoveIndex].bestMove !== moves[currentMoveIndex] && (
                                    <div style={{ 
                                        marginBottom: '1.5rem',
                                        padding: '1.25rem',
                                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                        border: '2px solid #bae6fd',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 6px -1px rgba(3, 105, 161, 0.1)'
                                    }}>
                                        <div style={{ 
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            color: '#0369a1',
                                            marginBottom: '0.75rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <span style={{ fontSize: '1.25rem' }}>💡</span>
                                            Best Move
                                        </div>
                                        <div style={{ 
                                            fontSize: '1.5rem',
                                            fontFamily: 'monospace',
                                            fontWeight: 700,
                                            color: '#0c4a6e',
                                            padding: '0.75rem',
                                            background: 'white',
                                            borderRadius: '8px',
                                            textAlign: 'center',
                                            border: '2px solid #bae6fd'
                                        }}>
                                            {analysis[currentMoveIndex].bestMove}
                                        </div>
                                    </div>
                                )}

                                <div style={{ 
                                    marginBottom: '1.5rem',
                                    padding: '1.25rem',
                                    background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                }}>
                                    <div style={{ 
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Position Evaluation
                                    </div>
                                    <div style={{ 
                                        fontSize: '2rem',
                                        fontWeight: 700,
                                        color: analysis[currentMoveIndex].evaluation && analysis[currentMoveIndex].evaluation! > 0 ? '#16a34a' : 
                                               analysis[currentMoveIndex].evaluation && analysis[currentMoveIndex].evaluation! < 0 ? '#dc2626' : '#111827',
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: '0.5rem'
                                    }}>
                                        {analysis[currentMoveIndex].evaluation !== undefined
                                            ? `${analysis[currentMoveIndex].evaluation! > 0 ? '+' : ''}${analysis[currentMoveIndex].evaluation!.toFixed(2)}`
                                            : 'N/A'}
                                        <span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#6b7280' }}>
                                            pawns
                                        </span>
                                    </div>
                                    {analysis[currentMoveIndex].evaluationDescription && (
                                        <div style={{ 
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            marginTop: '0.75rem',
                                            fontStyle: 'italic',
                                            padding: '0.5rem',
                                            background: 'white',
                                            borderRadius: '6px'
                                        }}>
                                            {analysis[currentMoveIndex].evaluationDescription}
                                        </div>
                                    )}
                                </div>

                                {(analysis[currentMoveIndex].analysis || analysis[currentMoveIndex].explanation) && (
                                    <div style={{ 
                                        padding: '1.25rem',
                                        background: analysis[currentMoveIndex].isBlunder ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' : 
                                                  analysis[currentMoveIndex].isMistake ? 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)' :
                                                  analysis[currentMoveIndex].isInaccuracy ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                        border: `2px solid ${analysis[currentMoveIndex].isBlunder ? '#fca5a5' : 
                                                              analysis[currentMoveIndex].isMistake ? '#fb923c' :
                                                              analysis[currentMoveIndex].isInaccuracy ? '#fcd34d' : '#86efac'}`,
                                        borderRadius: '12px',
                                        boxShadow: `0 4px 6px -1px ${analysis[currentMoveIndex].isBlunder ? 'rgba(220, 38, 38, 0.2)' : 
                                                              analysis[currentMoveIndex].isMistake ? 'rgba(234, 88, 12, 0.2)' :
                                                              analysis[currentMoveIndex].isInaccuracy ? 'rgba(245, 158, 11, 0.2)' : 'rgba(22, 163, 74, 0.2)'}`
                                    }}>
                                        <div style={{ 
                                            fontSize: '0.875rem',
                                            fontWeight: 700,
                                            color: analysis[currentMoveIndex].isBlunder ? '#991b1b' : 
                                                   analysis[currentMoveIndex].isMistake ? '#ea580c' :
                                                   analysis[currentMoveIndex].isInaccuracy ? '#f59e0b' : '#166534',
                                            marginBottom: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <span style={{ fontSize: '1.25rem' }}>
                                                {analysis[currentMoveIndex].isBlunder ? '❌' :
                                                 analysis[currentMoveIndex].isMistake ? '⚠️' :
                                                 analysis[currentMoveIndex].isInaccuracy ? '⚡' : '✅'}
                                            </span>
                                            {analysis[currentMoveIndex].isBlunder ? 'Why this is a blunder:' :
                                             analysis[currentMoveIndex].isMistake ? 'Why this is a mistake:' :
                                             analysis[currentMoveIndex].isInaccuracy ? 'Why this is an inaccuracy:' :
                                             'Analysis:'}
                                        </div>
                                        <div style={{ 
                                            fontSize: '0.875rem',
                                            color: analysis[currentMoveIndex].isBlunder ? '#991b1b' : 
                                                   analysis[currentMoveIndex].isMistake ? '#ea580c' :
                                                   analysis[currentMoveIndex].isInaccuracy ? '#f59e0b' : '#166534',
                                            lineHeight: '1.7',
                                            fontWeight: 500
                                        }}>
                                            {analysis[currentMoveIndex].analysis || analysis[currentMoveIndex].explanation}
                                        </div>
                                    </div>
                                )}

                                {!analysis[currentMoveIndex].analysis && !analysis[currentMoveIndex].explanation && analysis[currentMoveIndex].comment && (
                                    <div style={{ 
                                        padding: '1.25rem',
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                        borderRadius: '12px',
                                        fontSize: '0.875rem',
                                        color: '#374151',
                                        border: '1px solid #e5e7eb',
                                        lineHeight: '1.7'
                                    }}>
                                        {analysis[currentMoveIndex].comment}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyzeGameView;

